import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getAddOnService, updateBillingGroupOnAddOnService } from "../../api/AddOnServices";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups"; // Import the API function

const EditAddOnServicePage = () => {
  const { getAccessToken } = useAuth();
  const { addOnServicePublicId, agreementPublicId } = useParams();
  const navigate = useNavigate();
  const [addOnService, setAddOnService] = useState(null);
  const [loadingAddOnService, setLoadingAddOnService] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);

  useEffect(() => {
    handleGetAddOnService();
    handleGetBillingGroups(); // Fetch billing groups
  }, []);

  const handleGetAddOnService = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAddOnService = await getAddOnService(addOnServicePublicId, accessToken); // Hämta rätt user med API-anrop
      setAddOnService(newAddOnService);
      setLoadingAddOnService(false);
    } catch (error) {
      console.error("Failed to fetch AddOnService:", error);
      setLoadingAddOnService(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByAgreement(
        agreementPublicId,
        accessToken
      );
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

/*   const handleBillingGroupChange = (option) => {
    const selectedGroup = billingGroups.find((group) => group.name === option);
    if (!selectedGroup) return;
    setAddOnService((prevAddOnService) => ({
      ...prevAddOnService,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  }; */

  const handleUpdateAddOnService = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        console.error("No Access Token! User might be logged out.");
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      // Update the billing group first (if it has changed)
      if (!addOnService?.billingGroupPublicId || !addOnService?.addOnServicePublicId) {
        console.error("Missing billingGroupPublicId or addOnServicePublicId");
        toast.error("Missing required fields. Cannot update AddOnService.");
        return;
      }

      const addOnServiceUpdateData = {
        billingGroupPublicId: addOnService.billingGroupPublicId,
        addOnServicePublicId: addOnService.addOnServicePublicId,
        updatedDateTimeUTC: new Date().toISOString(), // Generates current timestamp
      };

      // Call API with correct payload
      const updatedAddOnService = await updateBillingGroupOnAddOnService(
        addOnServiceUpdateData,
        accessToken
      );

      // Update state with new license data
      setAddOnService(updatedAddOnService);

      setHasChanges(false); // Reset change state
      toast.success("Add-on service updated successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to update Add-on service:", error);
      toast.error("Failed to update Add-on service");
    }
  };

  const saveAndExit = () => {
    handleUpdateAddOnService();
  };
  

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Add-on service"} loading={loadingAddOnService} />
          </div>
          <div className={`${loadingAddOnService ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit Add-on service: {addOnService?.name}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={saveAndExit}
                >
                  Update
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={addOnService || {}}
                setData={setAddOnService}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "billingGroupName",
                    label: "Billing Group",
                    type: "dropdown",
                    disabled: false,
                  },
                  {
                    key: "comment",
                    label: "Comment",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "name",
                    label: "Name",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "addOnServicePublicId",
                    label: "Add-on service ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "price",
                    label: "Price",
                    type: "number",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                ]}
                dropdownOptions={{
                  billingGroupName: billingGroups.map((g) => ({
                    name: g.name, // Ensure "name" field exists
                    billingGroupPublicId: g.billingGroupPublicId, // Ensure ID field exists
                  })),
                }}
              />

              {/* <ProductForm
                product={product || {}}
                setProduct={setProduct}
                setHasChanges={setHasChanges}
                disabled={true}
                billingGroups={billingGroups}
                onBillingGroupChange={handleBillingGroupChange}
              /> */}
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditAddOnServicePage;
